// session-watcher.ts — proaktiver Session-Expiry-Timer (Lever B). Interaktions-
// UNABHÄNGIG: liest die verbleibende Token-Lebensdauer von `${apiBase}/auth/
// session` (Feld `expiresInSeconds`, vom Aggregator/Host gesetzt), setzt einen
// präzisen Timer und feuert bei Ablauf — fängt damit auch den Idle-Fall (gar
// keine Interaktion), den der reaktive apiFetch-/Nav-401-Pfad (Lever A) nicht
// abdeckt.
//
// Token-Refresh: der Timer hardcodet die Ablaufzeit NICHT. Bei jedem Feuern
// macht er einen Re-Probe auf `/auth/session`, der die AKTUELLE Token-`exp`
// liest — wurde der Token verlängert, liefert der Probe eine neue, längere TTL
// → reschedule statt Banner. Refresh „funktioniert einfach".
//
// Fail-safe (wie der apiFetch-Verifier, PFS-467 — Richtung „User drin lassen"):
//   - status 401            → Session definitiv tot → Banner + Auto-Logout.
//   - status 200            → lebt → (re)schedule aus expiresInSeconds.
//   - status 0/404/5xx       → inconclusive (Standalone ohne Probe, transienter
//                              Fehler) → NIE Banner; initial = nicht watchen,
//                              laufend = weiter pollen.
// Der Banner wird NUR im scheduled `onFire` ausgelöst (= bekannte Session ist
// abgelaufen), NIE im Initial-Probe — ein anonymer Login-Seiten-Aufruf (401)
// startet keinen Banner.

import { requestSource } from "./context-store.js";
import { withApiBase } from "./api-fetch.js";
import { dispatchSessionExpiredBanner } from "./banner-bus.js";

/**
 * SessionProbeResponse — der HOST-CONTRACT für `${apiBase}/auth/session`.
 *
 * Pflicht (schon seit PFS-467, vom apiFetch-Verifier genutzt):
 *   - HTTP 200 = Session lebt, HTTP 401 = Session tot. Body egal für die Liveness.
 *
 * OPTIONALER Enhancement für den proaktiven Idle-Timer (Lever B): bei 200 das
 * Feld `expiresInSeconds` (verbleibende Token-Lebensdauer) mitschicken. DANN
 * setzt der Watcher einen präzisen Timer (feuert genau bei Ablauf, auch ohne
 * Interaktion). OHNE das Feld degradiert er graceful auf einen periodischen
 * Poll — der Host MUSS es also nicht implementieren, gewinnt aber Präzision.
 *
 * Damit ist der Contract explizit + typisiert (nicht implizit): ein Host, der
 * den präzisen Timer will, implementiert dieses Feld bewusst; tut er's nicht,
 * funktioniert der Watcher trotzdem.
 */
export interface SessionProbeResponse {
  authenticated: boolean;
  /** Verbleibende Token-Lebensdauer in Sekunden. Fehlt/0 → Watcher pollt. */
  expiresInSeconds?: number;
}

const FALLBACK_POLL_SECONDS = 60; // expiresInSeconds unbekannt (0) → periodisch nachsehen
const FIRE_BUFFER_SECONDS = 2; // nach exp re-proben, damit die tote Session sicher sichtbar ist
const MIN_SCHEDULE_SECONDS = 5; // nie schneller takten als das

interface ProbeResult {
  status: number; // 0 = Netzwerkfehler/inconclusive, sonst HTTP-Status
  expiresInSeconds: number; // 0 = unbekannt
}

async function probeSession(): Promise<ProbeResult> {
  let res: Response;
  try {
    res = await fetch(withApiBase("/auth/session"), {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { status: 0, expiresInSeconds: 0 };
  }
  let exp = 0;
  if (res.status === 200) {
    try {
      const body = (await res.json()) as SessionProbeResponse;
      if (typeof body.expiresInSeconds === "number" && body.expiresInSeconds > 0) {
        exp = body.expiresInSeconds;
      }
    } catch {
      // kein JSON / kein Feld → exp bleibt 0 (Fallback-Poll)
    }
  }
  return { status: res.status, expiresInSeconds: exp };
}

/**
 * startSessionExpiryWatcher — startet den proaktiven Timer und returnt eine
 * Stop-Funktion (für `ctx.cleanup`). Der Aufrufer (pfs-shell) startet genau
 * einmal pro Connect.
 */
export function startSessionExpiryWatcher(): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const scheduleFrom = (r: ProbeResult): void => {
    if (stopped) return;
    const secs =
      r.status === 200 && r.expiresInSeconds > 0
        ? r.expiresInSeconds + FIRE_BUFFER_SECONDS
        : FALLBACK_POLL_SECONDS;
    timer = setTimeout(() => void onFire(), Math.max(MIN_SCHEDULE_SECONDS, secs) * 1000);
  };

  const onFire = async (): Promise<void> => {
    if (stopped) return;
    const r = await probeSession();
    if (stopped) return;
    if (r.status === 401) {
      // Bekannte Session ist abgelaufen → Banner + Auto-Logout. Kein reschedule.
      dispatchSessionExpiredBanner();
      stopped = true;
      return;
    }
    // lebt (200) oder inconclusive → weiter beobachten. Bei Refresh hat der
    // Probe gerade die neue, längere TTL gelesen.
    scheduleFrom(r);
  };

  // PFS-561: Vor der ersten Probe warten, bis der Host die `apiBase`-Source
  // registriert hat. Im WP-Embed laedt das pfs-components-Bundle (definiert
  // <pfs-shell> → connectedCallback → dieser Watcher) VOR dem Host-Bundle
  // (mednet-main.js), das `apiBase` per provideSource erst auf Modul-Eval
  // registriert. Ohne das Warten liest die erste Probe apiBase="" → /auth/session
  // am ROOT → 404 → inconclusive → der Watcher schedule't NIE (Lever B im Embed
  // tot, plus 96kB-404-Noise). Standalone: die Source ist sofort da (returnt "")
  // → kein Warten. Host ohne apiBase-Source: nach dem Timeout probt er trotzdem
  // (graceful wie bisher). requestSource liefert null wenn (noch) kein Provider.
  const apiBaseSourceReady = (): boolean =>
    typeof document !== "undefined" &&
    !!document.body &&
    requestSource(document.body, "apiBase") !== null;

  void (async () => {
    for (let i = 0; i < 40 && !apiBaseSourceReady() && !stopped; i++) {
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    if (stopped) return;
    const r = await probeSession();
    if (stopped) return;
    // Initial NUR bei aktiver Session (200) watchen. 401 = anonym (Login-Seite)
    // → kein Banner, kein Timer. Inconclusive (kein /auth/session, echtes
    // Standalone) → nicht watchen; Lever A deckt diese Fälle reaktiv ab.
    if (r.status === 200) scheduleFrom(r);
  })();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
